import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

// Pricing configuration
const ANNUAL_DISCOUNT = 0.20 // 20% discount for annual billing

const tiers = [
  {
    name: 'Free',
    monthlyPrice: 0,
    description: 'Good for one event you download right away',
    features: [
      '2 active drops',
      '1 day or 1 week time limits',
      { label: 'Photos deleted 2 weeks after the drop closes', limit: true },
      { label: '50MB per guest — about 10-15 phone photos', limit: true },
      { label: 'Photos resized to 2000x2000', limit: true },
      'Location and camera data removed',
    ],
    cta: 'Get Started',
    highlighted: false,
  },
  {
    name: 'Pro',
    monthlyPrice: 9,
    description: 'For regular event hosts',
    features: [
      '10 active drops',
      'Custom time limits (up to 1 month)',
      'Photos kept forever — never auto-deleted',
      '1GB per guest — about 200-300 phone photos',
      'Original full-resolution photos',
      'Keeps dates and location, so photos sort correctly',
      'Custom branding (colors, logo)',
      'Custom thank you message',
    ],
    cta: 'Start Free Trial',
    highlighted: true,
  },
  {
    name: 'Business',
    monthlyPrice: 49,
    description: 'For professional photographers & agencies',
    features: [
      '1,000 active drops',
      'Custom time limits (up to 1 month)',
      'Photos kept forever — never auto-deleted',
      '20GB per guest',
      'Original full-resolution photos',
      'Keeps dates and location, so photos sort correctly',
      'Custom branding (colors, logo)',
      'Custom thank you message',
      '10 team members included',
      'Role-based permissions',
    ],
    cta: 'Contact Sales',
    highlighted: false,
  },
]

export default function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly')

  const getPrice = (monthlyPrice: number) => {
    if (monthlyPrice === 0) return '$0'
    if (billingPeriod === 'annual') {
      const annualPrice = Math.round(monthlyPrice * 12 * (1 - ANNUAL_DISCOUNT))
      return `$${annualPrice}`
    }
    return `$${monthlyPrice}`
  }

  const getPeriodLabel = (monthlyPrice: number) => {
    if (monthlyPrice === 0) return null
    return billingPeriod === 'annual' ? '/year' : '/month'
  }

  const getSavings = (monthlyPrice: number) => {
    if (monthlyPrice === 0) return null
    const annualSavings = Math.round(monthlyPrice * 12 * ANNUAL_DISCOUNT)
    return annualSavings
  }

  return (
    <div className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold">Simple, transparent pricing</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Choose the plan that works for you
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <span
              className={cn(
                "text-sm font-medium transition-colors",
                billingPeriod === 'monthly' ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className={cn(
                "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                billingPeriod === 'annual' ? 'bg-primary' : 'bg-muted'
              )}
              role="switch"
              aria-checked={billingPeriod === 'annual'}
              aria-label="Toggle billing period"
            >
              <span
                className={cn(
                  "inline-block h-4 w-4 transform rounded-full bg-foreground transition-transform",
                  billingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
            <span
              className={cn(
                "text-sm font-medium transition-colors",
                billingPeriod === 'annual' ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              Annual
            </span>
            {billingPeriod === 'annual' && (
              <span className="ml-2 inline-flex items-center rounded-full bg-secondary/15 px-2.5 py-0.5 text-xs font-medium text-secondary">
                Save 20%
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                "rounded-lg border bg-card text-card-foreground p-8 relative",
                tier.highlighted && "border-primary shadow-lg ring-1 ring-primary"
              )}
            >
              {tier.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}
              <h3 className="text-xl font-semibold">{tier.name}</h3>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-bold">{getPrice(tier.monthlyPrice)}</span>
                {getPeriodLabel(tier.monthlyPrice) && (
                  <span className="ml-1 text-muted-foreground">{getPeriodLabel(tier.monthlyPrice)}</span>
                )}
              </div>
              {billingPeriod === 'annual' && tier.monthlyPrice > 0 && (
                <p className="mt-1 text-sm text-green-600 font-medium">
                  Save ${getSavings(tier.monthlyPrice)} per year
                </p>
              )}
              <p className="mt-2 text-sm text-muted-foreground">{tier.description}</p>

              <ul className="mt-6 space-y-3">
                {tier.features.map((feature) => {
                  // A uniform green check made "Photos deleted 2 weeks after the drop
                  // closes" read as something the plan includes. Limits get their own
                  // mark so a skimmer reads the constraint as a constraint.
                  const { label, limit } =
                    typeof feature === 'string' ? { label: feature, limit: false } : feature
                  return (
                    <li key={label} className="flex items-start">
                      {limit ? (
                        <Minus
                          className="h-5 w-5 text-muted-foreground shrink-0 mr-2"
                          aria-hidden="true"
                        />
                      ) : (
                        <Check className="h-5 w-5 text-primary shrink-0 mr-2" aria-hidden="true" />
                      )}
                      <span className={`text-sm${limit ? ' text-muted-foreground' : ''}`}>
                        <span className="sr-only">{limit ? 'Limitation: ' : 'Included: '}</span>
                        {label}
                      </span>
                    </li>
                  )
                })}
              </ul>

              <Button
                className="w-full mt-8"
                variant={tier.highlighted ? 'default' : 'outline'}
                asChild
              >
                <Link to="/signup">{tier.cta}</Link>
              </Button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-card text-card-foreground rounded-lg border p-6">
              <h3 className="font-semibold">Can I switch between monthly and annual billing?</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Yes! You can switch between billing periods at any time. When switching from monthly to annual,
                you'll be credited for any remaining time on your current plan. The 20% annual discount is
                applied automatically when you choose annual billing.
              </p>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border p-6">
              <h3 className="font-semibold">What happens if I exceed my drop limit?</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                You'll need to close an existing drop or upgrade to a higher tier to create more drops.
                Your existing drops and photos are never deleted due to reaching the limit.
              </p>
            </div>
            <div className="bg-card text-card-foreground rounded-lg border p-6">
              <h3 className="font-semibold">Is there a free trial?</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Yes! Free tier users get one drop with Pro features to try out the premium experience.
                No credit card required to start.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
